import Vue from 'vue'
import { getInstance } from '@/plugins/auth'
import { ATOMIC_CUSTOMER_APIS } from '@/utils/environment'
import { ENVIRONMENTS, ERROR_CODES } from '@/utils/constants'
import store from '@/store'

export function interceptor(request, next) {
  const authService = getInstance()

  // If the API endpoint URL being used is one of the customer API ones, attached the bearer token
  if (ATOMIC_CUSTOMER_APIS.some((url) => request.url.startsWith(url))) {
    request.headers.set('Authorization', `Bearer ${authService.accessToken}`)
  }

  const activeCustomerId = localStorage.getItem('activeCustomerId')
  if (activeCustomerId) {
    request.headers.set('x-active-customer-id', activeCustomerId)
  }

  const email = authService.getEmail()
  if (email) {
    request.headers.set('x-user-email', email)
  }

  // Attach an active environment header for accurate authorization while working locally
  if (process.env.NODE_ENV === ENVIRONMENTS.DEVELOPMENT) {
    request.headers.set('x-active-environment', store.state.environment.activeEnvironment)
  }

  next(function (response) {
    if (response.status === 428) {
      // this is for sso or self-signup users that didn't have a customer associated with them;
      // we've already updated the user with a customer, but they need to get a new token
      return (
        authService
          // if the user clicks 'remember me' the session will still be valid and we can retry with a new token
          .getTokenSilently({
            ignoreCache: true,
          })
          .then((freshToken) => {
            authService.accessToken = freshToken
            request.headers.set('Authorization', `Bearer ${freshToken}`)
            return Vue.http(request)
          })
          .catch(() => {
            // If the user does not click 'remember me' they need to log in again
            return authService
              .loginWithRedirect({
              })
              .catch(() => {
                authService.logout({ shouldClearActiveCustomerId: true })
              })
          })
      )
    } else if (response.status === 401) {
      // The API answers a request for a task owned by a different customer with a 401, which
      // happens after switching customers (e.g. back button to a task detail page). The session
      // is still valid, so let the caller handle it instead of logging the user out.
      if (response.body?.code !== ERROR_CODES.NOT_AUTHORIZED_FOR_TASK) {
        authService.logout({ shouldClearActiveCustomerId: true })
      }
    } else if (response.status === 403) {
      const logoutTimeout = setTimeout(() => {
        authService.logout({ shouldClearActiveCustomerId: true })
      }, 20000)

      Vue.toasted.show(
        'Unable to access resource. Please contact your administrator for an invite to your organization or required role.',
        {
          duration: 20000,
          type: 'error',
          position: 'top-center',
          action: {
            text: 'OK',
            onClick: (e, toastObject) => {
              clearTimeout(logoutTimeout)
              toastObject.goAway(0)
              authService.logout({ shouldClearActiveCustomerId: true })
            },
          },
        }
      )
    } else if (
      response.status >= 400 &&
      response.status < 500 &&
      [ENVIRONMENTS.DEVELOPMENT, ENVIRONMENTS.TEST].includes(process.env.NODE_ENV)
    ) {
      console.log('Error occurred in console.', response)
    }

    return response
  })
}
